import { addDiagnosisApi } from 'api/DCK/addDiagnosisApi';
import { oauthApi } from 'api/Oauth/OauthApi';
import { governmentPaths, pagePaths, preciousChildPaths } from 'constants/path';
import { createUserWithEmailAndPassword, getAuth } from 'firebase/auth';
import { ValueType } from 'models/Common/commonTypes';
import { AddDiagnosisApiCallFunction, SignUpApiCallFunction, GovernmentAddDiagnosisApiCallFunction, MindupAddDiagnosisApiCallFunction } from 'models/DCK/DCKApiTypes';
import moment from 'moment';
import { mindUpDiagnosisApi } from 'api/MindUp/mindUpDiagnosisApi';
import { VoucherStatusParam } from 'models/Tablet/AssessmentApiTypes';
import { TabletAssessmentApi } from 'api/Tablet/TabletAssessmentApi';
import { calculateAge } from 'utils/calculateAge';
import { ChangeDiagnosisStatusRqData, VoucherAddDiagnosisRqData } from 'models/MindUp/MindUpTypes';
import { StorageKeys } from 'constants/storageKeys';

const normalizeBearerToken = (token: string) => token.replace(/^Bearer\s+/i, '').trim();

export const tokenHeader = (token: string) => {
    return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${normalizeBearerToken(token)}`,
    };
};

export const clAdminTokenHeader = (fallbackToken?: string) => {
    return tokenHeader(process.env.REACT_APP_CL_ADMIN_KEY || fallbackToken || '');
};

export const apiKeyHeader = (apiKey?: string) => {
    return {
        'Content-Type': 'application/json',
        Authorization: `key:${process.env.REACT_APP_AUTH_KEY}`,
    };
};

// 진료 추가 api call
export const addDiagnosisApiCall: AddDiagnosisApiCallFunction = async (value, loginData, selectedDate, selectedTime, navigate, setInit) => {
    const gender = value.gender === '1' || value.gender === '3' || value.gender === '5' || value.gender === '7' ? 'male' : 'female';
    const selectedMoment = moment(selectedDate, 'YYYY/MM/DD');
    const birthMoment = moment(value.birthDate, 'YYMMDD');
    const years = selectedMoment.diff(birthMoment, 'years');
    const months = selectedMoment.diff(birthMoment, 'months') - years * 12;

    const rqData = {
        patientCode: value.patientCode,
        patientName: value.patientName,
        patientMobile: value.patientMobile,
        patientIdentityNumber: value.birthDate + value.gender,
        patientGender: gender,
        gradeCode: value.grade,
        gradeName: value.schoolAge + ' ' + value.semester,
        hospitalId: loginData.hospitalId,
        hospitalName: loginData.hospitalName,
        doctorName: value.doctorName,
        patientMemo: value.patientMemo,
        diagnosisDateTime: moment(selectedDate).format('YYYYMMDD') + moment(selectedTime, 'A h:mm').format('HHmm'),
        diagnosisTimestamp: moment(selectedDate + ' ' + moment(selectedTime, 'A h:mm').format('HHmm'), 'YYYYMMDD HHmm').valueOf(),
        assessmentAge: `만 ${years}세 ${months}개월`,
        encryptedKey: value.encryptedKey,
    };

    const response = await addDiagnosisApi.addDiagnosis(rqData, loginData.token);

    if (response.status === 'ok' && response.data) {
        setInit();
        navigate(pagePaths.viewDetaildiagnosis, { state: response.data });
    } else {
        console.log('network error');
    }
};

// 성남 진료 추가 api call ????????? -> navigate 를 API CALL 로직에 넣는것이 어색해 보임 (UI 영역과 function 영역의 분리 필요) 해당 부분은 레거시라 수정하지 않음
export const governmentAddDiagnosisApiCall: GovernmentAddDiagnosisApiCallFunction = async (value, navigate, assessmentList, selectedData, info) => {
    const gender = value.gender === '1' || value.gender === '3' ? 'male' : 'female';
    const month = value.month.toString().padStart(2, '0');
    // const selectedMoment = moment(selectedDate, 'YYYY/MM/DD');
    // const birthMoment = moment(value.birthDate, 'YYMMDD');
    // const years = selectedMoment.diff(birthMoment, 'years');
    // const months = selectedMoment.diff(birthMoment, 'months') - years * 12;

    const rqData = {
        patientName: value.patientName,
        patientMobile: value.patientMobile,
        patientIdentityNumber: value.birthDate.slice(2, 4) + month + '01' + value.gender,
        patientGender: gender,
        gradeCode: value.grade,
        gradeName: value.schoolAge + ' ' + value.semester,
        hospitalId: info.hospitalId,
        hospitalName: '성남시병원',
        doctorName: '홍홍홍',
        patientMemo: value.patientMemo,
        diagnosisDateTime: moment().format('YYYYMMDD') + moment().format('HHmm'),
        diagnosisTimestamp: moment().valueOf(),
        assessList: assessmentList,
    };

    const response = await addDiagnosisApi.governmentAddDiagnosis(rqData, info.token);

    // ! navigate로 selectedData 넘겨주기
    if (response.status === 'ok' && response.data) {
        console.log(response, 'response');
        // setInit();
        navigate(governmentPaths.inspectionGuide, { replace: true, state: { data: selectedData, info: info, diagnosisInfo: response.data } });
    } else {
        console.log('network error');
    }
};

// 금쪽이 진료 추가 api call
export const mindupAddDiagnosisApiCall: MindupAddDiagnosisApiCallFunction = async (value, token, hospitalId) => {
    // 타입 가드: VoucherAddDiagnosisRqData인지 확인
    const isVoucherData = 'patientIdentityNumber' in value;

    let rqData;

    if (isVoucherData) {
        // VoucherAddDiagnosisRqData 타입인 경우
        const voucherValue = value as VoucherAddDiagnosisRqData;
        const encryptedKey = sessionStorage.getItem(StorageKeys.ENCRYPTED_DEVICE_KEY);

        rqData = {
            ...voucherValue,
            encryptedKey: encryptedKey || undefined,
        };
    } else {
        // ValueType인 경우 (기존 로직)
        const valueType = value as ValueType;
        const identityNumber = valueType.birthDate.toString().substring(2, 8) + valueType.gender;
        const hospitalInfoResponse = await addDiagnosisApi.hospitalFindById(hospitalId, token);
        const encryptedKey = sessionStorage.getItem(StorageKeys.ENCRYPTED_DEVICE_KEY);

        rqData = {
            patientName: valueType.patientName,
            parentName: valueType.parentName,
            patientMobile: valueType.patientMobile,
            patientIdentityNumber: identityNumber,
            patientGender: calculateAge(identityNumber).gender,
            gradeCode: valueType.grade,
            gradeName: valueType.schoolAge + ' ' + valueType.semester,
            relationType: Number(valueType.relationType),
            hospitalId: hospitalId,
            hospitalName: hospitalInfoResponse.data.name ?? '',
            doctorName: '',
            patientMemo: valueType.patientMemo,
            diagnosisDateTime: moment().format('YYYYMMDD') + moment().format('HHmm'),
            diagnosisTimestamp: moment().valueOf(),
            voucherCode: valueType.voucherCode,
            counselReason: valueType.counselReason,
            encryptedKey: encryptedKey || undefined,
        };
    }

    const response = await mindUpDiagnosisApi.addDiagnosis(rqData, token);

    if (response.status === 'ok' && response.data) {
        return response.data;
    } else {
        throw new Error('API 호출 실패: ' + response.data);
    }
};

// 진단 상태 변경 API 호출
export const changeDiagnosisStatus = async (diagnosisId: string, status: number, token: string): Promise<void> => {
    const changeRq: ChangeDiagnosisStatusRqData = {
        _id: diagnosisId,
        status: status,
    };

    const response = await mindUpDiagnosisApi.changeDiagnosisStatus(changeRq, token);
    if (response.status !== 'ok') {
        throw new Error('진단 상태 변경 실패');
    }
};

// TODO: 구성에 따라 나눠 관리할 필요가 있어보임
export const updateVoucherCodeStatus = async (voucherCode: string, status: number, token: string): Promise<void> => {
    const voucherParam: VoucherStatusParam = {
        code: voucherCode,
        status: status,
    };

    const response = await TabletAssessmentApi.chageVoucherStatus(voucherParam, token);
    if (response.status !== 'ok') {
        throw new Error('바우처 코드 상태 변경 실패');
    }
};

// 회원가입 api call
export const signUpApiCall: SignUpApiCallFunction = async (value, selectedHospital, setSignUpComplete, setErrMsg, setLoading, emailInputRef) => {
    const auth = getAuth();
    createUserWithEmailAndPassword(auth, value.email, value.password)
        .then(async (userCredential) => {
            const version = require('../../package.json').version;
            const user = userCredential.user;
            const idToken = await user.getIdToken();
            // const fcmToken = await getPermission();
            const rqData = {
                idToken: idToken,
                deviceType: 3,
                os: 'web',
                model: 'chrome',
                version: version,
                // token: fcmToken,
                token: 'Notification permission denied',
                mobile: value.phoneNumber,
                picture: '',
                name: value.name,
                hospitalId: selectedHospital?.id,
                hospitalName: selectedHospital?.name,
                level: selectedHospital?.id === '677e22fe3d73c33a066fc54a' ? 20 : 0,
            };
            const response = await oauthApi.oauth(rqData);
            if (response.status === 'ok') {
                setLoading(false);
                return setSignUpComplete(true);
            }
        })
        .catch((error) => {
            const errorCode = error.code;
            setLoading(false);
            if (errorCode === 'auth/email-already-in-use') {
                setErrMsg((prevErrMsg: ValueType) => ({ ...prevErrMsg, email: '이미 가입된 이메일입니다.' }));
                console.log('이미 가입된 이메일입니다.');
                emailInputRef.current?.focus();
            } else {
                console.log('회원가입에 실패하였습니다.');
            }
        });
};

// export const responseAiResult = async () => {
//     await openAiApi.responseAiResult();
// };

// Login api call
// export const loginApiCall: LoginApiCallFunction = async (value, setLoginData, setErrMsg, navigate, setLoading, emailRef, passwordRef) => {
//     // setLoading(true);
//     const auth = getAuth();
//     const response = await userApi.existUser(value.email);
//     if (response.status === 'ok') {
//         signInWithEmailAndPassword(auth, value.email, value.password)
//             .then(async (userCredential) => {
//                 const version = require('../../package.json').version;
//                 console.log(version);
//                 const user = userCredential.user;
//                 const idToken = await user.getIdToken();
//                 const fcmToken = await getPermission();
//                 const rqData = {
//                     idToken: idToken,
//                     deviceType: 3,
//                     os: 'web',
//                     model: 'chrome',
//                     version: version,
//                     token: fcmToken,
//                 };
//                 const response = await oauthApi.oauth(rqData);

//                 if (response.status === 'ok') {
//                     const getHospitalDataApiResponse = await addDiagnosisApi.hospitalFindById(response.data.user.hospitalId, response.data.bearerToken);
//                     if (getHospitalDataApiResponse.status === 'ok') {
//                         // TODO: token 테스트
//                         //    axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.bearerToken}`;

//                         setLoginData({
//                             id: response.data.user._id,
//                             email: response.data.user.email,
//                             hospitalName: response.data.user.hospitalName,
//                             hospitalId: response.data.user.hospitalId,
//                             level: response.data.user.level,
//                             mobile: response.data.user.mobile,
//                             name: response.data.user.name,
//                             status: response.data.user.status,
//                             token: response.data.bearerToken,
//                             doctors: getHospitalDataApiResponse.data.doctors,
//                             diagnosisTap: 0,
//                         });
//                         setLoading(false);

//                         if (response.data.user.status === AccountStatus.active) {
//                             if (response.data.user.level !== AccountLevel.admin) {
//                                 if (response.data.user.level === AccountLevel.sungnam) {
//                                     navigate(pagePaths.diagnosisList, { replace: true });
//                                 } else if (response.data.user.level === AccountLevel.externalCounselor) {
//                                     navigate(pagePaths.todayDiagnosis, { replace: true });
//                                 } else {
//                                     navigate(pagePaths.home, { replace: true });
//                                 }
//                             } else {
//                                 navigate(pagePaths.adminDiagnosisList, { replace: true });
//                             }
//                             return getHospitalDataApiResponse.data;
//                         } else {
//                             console.log('panding 상태, home으로 이동');
//                         }
//                     } else {
//                         console.log('HospitalData 가져오기 실패');
//                         setLoading(false);
//                     }
//                 } else {
//                     console.log('oauth 로그인 실패');
//                     setLoading(false);
//                 }
//             })
//             .catch((error) => {
//                 const errorCode = error.code;
//                 const errorMessage = error.message;
//                 console.log(errorCode, errorMessage, 'RtError');
//                 // 비밀번호 체크
//                 setErrMsg((prevErrMsg) => ({ ...prevErrMsg, password: LoginPageError.passwordCheckError }));
//                 passwordRef.current?.focus();
//                 setLoading(false);
//             });
//     } else {
//         // 아이디 중복체크로 아이디가 있는 아이디인지 없는 아이디인지 체크
//         setErrMsg((prevErrMsg) => ({ ...prevErrMsg, email: LoginPageError.loginIdExistanceCheckError }));
//         emailRef.current?.focus();
//         setLoading(false);
//     }
// };
